import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { type KnowledgeBaseRow } from './hooks/useKnowledgeBasePerformance';

const buildRows = (data: KnowledgeBaseRow[]) =>
  data.map((row) => ({
    content_title: row.title ?? row.documentId,
    knowledge_base: row.collection ?? row.collectionId,
    retrieved: row.retrieved,
    used: row.used,
  }));

interface KnowledgeBasePerformanceExportProps {
  data: KnowledgeBaseRow[];
}

export const KnowledgeBasePerformanceExport = ({ data }: KnowledgeBasePerformanceExportProps) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_AI_AGENTS, 'knowledge_base_performance', format);

        const rows = buildRows(data);
        const filename = buildExportFilename('knowledge_base_performance');

        if (format === 'json') {
          downloadJson(rows, filename);
          return;
        }
        downloadCsv(rows, filename);
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [data, t]
  );

  return <ExportButton variant="icon" onExport={handleExport} isExporting={isExporting} />;
};
